import { useQuery } from '@tanstack/react-query';
import { AxiosResponse } from 'axios';
import { isEmpty, isNil, isString, omitBy } from 'lodash';

import {
	ConsentStatusDto,
	CreateEndCustomerCommand,
	EndCustomerSearchResponseDto,
	UpdateEndCustomerCommand,
	useProductCenterApi,
} from 'api';
import { telephoneCountryCodesQuery } from 'api/queries';

import {
	EndCustomerSearchResponse,
	EndCustomerWithLanguageDto,
	STORAGE_CONSENT_LEGAL_REFERENCE,
	SearchEndCustomersQueryData,
	SearchFormData,
} from './types';

export const isAnonymous = (consentApprovals: ConsentStatusDto[] | null) =>
	!!consentApprovals?.find(
		({ legalReference, value }) =>
			legalReference === STORAGE_CONSENT_LEGAL_REFERENCE && !value
	);

const mapEndCustomerToRequestData = ({
	consentApprovals,
	notificationLanguage,
	...rest
}: EndCustomerWithLanguageDto) => ({
	consentApprovals:
		consentApprovals?.map(({ legalReference, value }) => ({
			legalReference,
			value: value ?? false,
		})) ?? [],
	...(isAnonymous(consentApprovals)
		? {
				countryCode: rest.countryCode,
				city: rest.city,
				postalCode: rest.postalCode,
			}
		: rest),
});

export const mapEndCustomerToCreateRequestData = (
	data: EndCustomerWithLanguageDto
) =>
	({
		...mapEndCustomerToRequestData(data),
		notificationLanguage: data.notificationLanguage,
	}) as CreateEndCustomerCommand;

export const mapEndCustomerToUpdateRequestData = (
	data: EndCustomerWithLanguageDto
) =>
	({
		...mapEndCustomerToRequestData(data),
		id: data.id,
	}) as UpdateEndCustomerCommand;

export const getFullName = (
	customer: EndCustomerWithLanguageDto | null | undefined
) => {
	if (customer == null) {
		return '';
	}

	return `${customer.firstname || ''} ${customer.surname || ''}`;
};

export const usePhoneNumberWithPrefix = (
	customer: EndCustomerWithLanguageDto | null | undefined
) => {
	const { api } = useProductCenterApi();
	const { data } = useQuery({
		...telephoneCountryCodesQuery(api),
		enabled: !!customer?.phonePrefixCountryCode,
	});

	const phoneNumber = customer?.phoneNumber ?? '';
	if (customer?.phonePrefixCountryCode == null) {
		return phoneNumber;
	}

	return `${data?.find(({ shortCountryCode }) => shortCountryCode === customer.phonePrefixCountryCode)?.telephoneCode ?? customer.phonePrefixCountryCode} ${phoneNumber}`;
};

export const getNewSearchResults = (
	searchData: EndCustomerSearchResponse | null,
	updatedCustomer: EndCustomerWithLanguageDto,
	wasDeleted: boolean
): EndCustomerSearchResponse | null => {
	if (!searchData?.results) {
		return searchData;
	}

	const { results, hasMoreResults } = searchData;

	return {
		results: wasDeleted
			? results.filter((customer) => customer.id !== updatedCustomer.id)
			: results.map((item) =>
					item.id === updatedCustomer.id ? updatedCustomer : item
				),
		hasMoreResults,
	};
};

export const hasOwnerAndOperatorSameEmail = (
	owner: EndCustomerWithLanguageDto | null,
	operator: EndCustomerWithLanguageDto
) => owner?.email === operator.email;

export const mapSearch = ({
	data: { hasMoreResults, endCustomers },
}: AxiosResponse<EndCustomerSearchResponseDto>): EndCustomerSearchResponse => ({
	results: endCustomers as EndCustomerWithLanguageDto[],
	hasMoreResults: hasMoreResults,
});

export const mapSearchFormDataToQueryData = ({
	phonePrefix,
	phoneNumber,
	surNameOrLastName,
	zipCode,
	address,
	...rest
}: SearchFormData): SearchEndCustomersQueryData =>
	omitBy(
		{
			phonePrefixCountryCode: phonePrefix,
			phone: phoneNumber,
			surname: surNameOrLastName,
			postalCode: zipCode,
			addressFirstPart: address,
			...rest,
		},
		(value) => isNil(value) || (isString(value) && isEmpty(value))
	);

// returns accepted by ECM country code
export const mapUserLanguageToConsentLanguage = (language: string) => {
	const map: Record<string, string> = {
		'zh-Hans': 'zh-CN',
	};

	return map[language] ?? language;
};
